import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {Amplify, Auth, Hub} from 'aws-amplify';
import {jwtDecode} from 'jwt-decode';
import {LoginUser, User} from '../models/user';
import {CognitoUserService} from '../services/cognito-user.service';
import {
  HOSTED_UI_SIGN_IN_FLAG,
  LOGIN_PATH,
  REFRESH_TOKEN_EXPIRY_MS,
  REFRESH_TOKEN_INTERVAL,
  SESSION_EXPIRED_MESSAGE,
  TOKEN_KEY,
  USER_NOT_ACTIVE,
} from '../common/Constants';
import {environment} from '../../environments/environment';
import {MessageHandlerService} from '../services/message-handler.service';
import {Router} from '@angular/router';
import {UserDataManager} from '../user/UserDataManager';
import {LocalStorageService} from '../services/local-storage.service';
import {UtilService} from '../services/util/util.service';
import {TitleService} from '../services/title.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  public authenticationSubject: BehaviorSubject<any>;
  public currentUserSubject: BehaviorSubject<any>;
  public currentUser: Observable<any>;
  userActive: boolean = false;
  loggedInUser: User;
  refreshTokenInterval: number = REFRESH_TOKEN_INTERVAL;
  lastActivityTimestamp: number;
  refreshTokenTimeout: any;
  subscribers: Subscription[] = [];

  constructor(
    private cognitoUserService: CognitoUserService,
    private messageHandlerService: MessageHandlerService,
    private router: Router,
    private localStorageService: LocalStorageService,
    public userDataManager: UserDataManager,
    private utilService: UtilService,
    private titleService: TitleService
  ) {
    Amplify.configure({
      Auth: environment.cognito,
    });

    this.authenticationSubject = new BehaviorSubject<boolean>(false);
    this.cognitoUserService = new CognitoUserService();

    this.currentUserSubject = new BehaviorSubject<any>(<string>localStorage.getItem(TOKEN_KEY));
    this.currentUser = this.currentUserSubject.asObservable();
    this.lastActivityTimestamp = Date.now();
    this.initTokenRefresh();
    this.getCurrentUser();
  }

  get authenticated() {
    return this.getToken() != null;
  }

  initTokenRefresh(): void {
    document.addEventListener('mousemove', this.setUserActive);
    document.addEventListener('keydown', this.setUserActive);
    document.addEventListener('touchstart', this.setUserActive);
    document.addEventListener('keyup', this.setUserActive);
    this.startTokenRefreshInterval();
  }
  startTokenRefreshInterval(): void {
    clearInterval(this.refreshTokenTimeout);
    if (this.userActive) {
      this.refreshTokenTimeout = setInterval(() => {
        const timeSinceLastActivity = Date.now() - this.lastActivityTimestamp;
        if (timeSinceLastActivity <= this.refreshTokenInterval) {
          this.refreshToken().catch((error) => {
            // Only an answer that the session is over signs the user out. A network blip (e.g. a laptop
            // waking from sleep) or Cognito throttling used to end it too, in every open tab; the
            // interval keeps running, so the next tick tries again while the access token is still valid.
            if (this.isSessionEndedError(error)) {
              this.handleSessionExpired(error);
            } else {
              console.log('Token refresh failed, will retry on the next tick: ', error);
            }
          });
        } else {
          this.startTokenRefreshInterval();
        }
      }, this.refreshTokenInterval);
    }
  }

  /**
   * True when Cognito rejected a sign-in or REFRESH_TOKEN_AUTH attempt because the account
   * itself has been disabled (e.g. via AdminDisableUser) — AWS can do this automatically —
   * as opposed to an ordinary expired/invalid refresh token or another auth failure. Cognito
   * reports both as the same NotAuthorizedException code, distinguishable only by message text.
   */
  isCognitoUserDisabledError(error: any): boolean {
    return error?.code === 'NotAuthorizedException' && /disabled/i.test(error?.message || '');
  }

  /**
   * True when a REFRESH_TOKEN_AUTH attempt failed because the refresh token itself has expired
   * or is invalid — Cognito's normal, expected NotAuthorizedException once a session ages out
   * (message text along the lines of "Refresh Token has expired" / "Invalid Refresh Token").
   * This is the ordinary case, not an account being disabled.
   *
   * Checked purely from the error the failed refresh call already returned — no extra Cognito
   * call (e.g. GetUser/AdminGetUser) is made just to probe whether the account was disabled.
   * That confirmation comes for free from Cognito's own message text.
   */
  isRefreshTokenExpiredError(error: any): boolean {
    const message: string = error?.message || '';
    return error?.code === 'NotAuthorizedException' && /refresh\s*token/i.test(message) && /expired|invalid/i.test(message);
  }

  /**
   * True when a failed refresh means the session really is over: Cognito refused it
   * (NotAuthorizedException - refresh token expired, invalid or revoked, or the user disabled -
   * or UserNotFoundException), or Amplify holds no signed-in user any more (e.g. signed out in
   * another tab). False for a network error, throttling or any other failure that a later try can get
   * past, so those never sign the user out.
   */
  isSessionEndedError(error: any): boolean {
    if (error === 'No current user' || error?.name === 'AuthError') return true;
    return error?.code === 'NotAuthorizedException' || error?.code === 'UserNotFoundException';
  }

  /**
   * A REFRESH_TOKEN_AUTH failure — whether from a disabled user or a refresh token that has
   * simply expired/is invalid — always surfaces here as a NotAuthorizedException from
   * refreshToken(). All three cases end the session the same way and show the same generic
   * toast to the user (a disabled account is the one exception with its own message); the
   * classification below exists so an *unrecognized* error shape — one that's neither a
   * confirmed disabled-account nor a confirmed expired-refresh-token — gets logged instead of
   * silently looking identical to a routine expiry.
   */
  private handleSessionExpired(error?: any): void {
    clearInterval(this.refreshTokenTimeout);
    this.clear();
    this.authenticationSubject.next(false);
    this.titleService.setTitle(false);

    let message: string;
    if (this.isCognitoUserDisabledError(error)) {
      message = USER_NOT_ACTIVE;
    } else if (this.isRefreshTokenExpiredError(error)) {
      message = SESSION_EXPIRED_MESSAGE;
    } else {
      console.log('Session ended for an unrecognized reason: ', error);
      message = SESSION_EXPIRED_MESSAGE;
    }

    this.messageHandlerService.generateToastMessage('error', '', message);
    this.router.navigate([LOGIN_PATH]);
  }

  ngOnDestroy(): void {
    this.clear();
    clearInterval(this.refreshTokenTimeout);
  }

  setUserActive = (): void => {
    // The refresh starts only for an active user, and on a page load this service is built before any
    // activity, so after a reload it never started and the stored token ran out within the hour however
    // busy the user was (IOHB-1983). The first activity of a signed-in user starts it; the sign-in page
    // has no session to refresh.
    const wasIdle = !this.userActive;
    this.userActive = true;
    this.lastActivityTimestamp = Date.now();
    if (wasIdle && this.authenticated) {
      this.startTokenRefreshInterval();
    }
  };

  /**
   * Reads Cognito's `auth_time` claim (Unix seconds) from the currently stored access token —
   * the timestamp of the user's original sign-in, which stays fixed across token refreshes
   * within the same session (unlike `iat`, which changes on every refresh). Returns null if
   * there's no stored token or it can't be decoded, meaning "unknown", not "not expired".
   */
  private getAuthTimeMs(): number | null {
    const token = this.getToken();
    if (!token) return null;
    try {
      const payload: any = jwtDecode(token);
      return typeof payload?.auth_time === 'number' ? payload.auth_time * 1000 : null;
    } catch {
      return null;
    }
  }

  /**
   * True once enough time has passed since the user's original sign-in that Cognito's refresh
   * token for this session is *probably* expired — estimated client-side from `auth_time`,
   * compared against this environment's hand-configured REFRESH_TOKEN_EXPIRY_MS. That constant
   * is a guess, not a value read from Cognito (there's no client-side API for a user's own App
   * Client's refresh-token validity) — if it's ever out of sync with the real Cognito setting,
   * this is wrong in either direction. So it must never by itself end a session or decide not
   * to ask Cognito; it's only used to gate cheap local cleanup *after* Cognito has already
   * confirmed a session is dead, so a stale token in storage stops causing repeat sign-in
   * attempts (see isAuthenticated()) (IOHB-NA).
   *
   * Returns false — i.e. "assume still valid" — whenever this can't be estimated locally (no
   * stored token, or an unexpected token shape).
   */
  isRefreshTokenDefinitelyExpired(): boolean {
    const authTime = this.getAuthTimeMs();
    if (authTime == null) return false;
    return Date.now() - authTime > REFRESH_TOKEN_EXPIRY_MS;
  }

  /** True where "Login via SSO" uses the Hosted UI code flow; elsewhere it keeps the backend's token-only link. */
  get ssoCodeFlowEnabled(): boolean {
    return !!environment.cognito?.sso;
  }

  /**
   * Turns on Amplify's Hosted UI handling, only for the two SSO steps. Once on, Amplify treats any ?code= in the
   * URL as a sign-in callback and, when the exchange fails, rewrites the URL to /authorize - and the e-sign, ioDoc
   * and report verification links and the password reset link all carry a ?code=. So it stays off at start-up.
   */
  private configureHostedUi(): void {
    const sso = environment.cognito.sso;
    Auth.configure({
      oauth: {
        domain: sso.domain,
        scope: sso.scope,
        redirectSignIn: `${window.location.origin}/authorize`,
        redirectSignOut: `${window.location.origin}/login`,
        responseType: 'code',
      },
    });
  }

  /** "Login via SSO": sends the browser to the identity provider. Amplify keeps the PKCE verifier for the return. */
  signInWithSso(): Promise<any> {
    this.configureHostedUi();
    return Auth.federatedSignIn({ customProvider: environment.cognito.sso.identityProvider });
  }

  /**
   * On /authorize, after the identity provider: Amplify swaps the code for the session and keeps it like a password
   * sign-in, refresh token included, so the token refresh keeps the user signed in. Resolves with the access token
   * and the user's email; rejects when the sign-in failed or the page was opened without a sign-in response.
   */
  completeSsoSignIn(url: string = window.location.href): Promise<{ accessToken: string; email: string }> {
    const params = new URL(url).searchParams;
    if (!params.has('code') && !params.has('error')) {
      return Promise.reject(new Error('No sign-in response in the URL'));
    }
    return new Promise((resolve, reject) => {
      const stopListening = Hub.listen('auth', ({ payload }) => {
        if (payload.event === 'cognitoHostedUI') {
          stopListening();
          // Amplify's own listener, registered when Amplify loads, has just set this flag. With it and the Hosted
          // UI handler still in memory, Auth.signOut() redirects to Cognito's /logout and never resolves, so the
          // portal's logout cleanup never ran and the access token stayed behind. Without it, sign-out works as it
          // does after any page load: tokens revoked, then the portal's sso-logout redirect.
          localStorage.removeItem(HOSTED_UI_SIGN_IN_FLAG);
          const session = payload.data.getSignInUserSession();
          resolve({
            accessToken: session.getAccessToken().getJwtToken(),
            email: session.getIdToken().decodePayload()?.email,
          });
        } else if (payload.event === 'cognitoHostedUI_failure') {
          stopListening();
          reject(payload.data);
        }
      });
      // Amplify reads the code from the URL as it is configured, and reports the outcome on the Hub.
      this.configureHostedUi();
    });
  }

  async refreshToken(): Promise<any> {
    try {
      const newTokan = await Auth.currentAuthenticatedUser({ bypassCache: true });
      this.setToken(newTokan.signInUserSession.accessToken.jwtToken);
      this.startTokenRefreshInterval();
      return await Promise.resolve();
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  signIn(user: LoginUser) {
    this.clear();
    return Auth.signIn(user.email, user.password)
      .then((data) => {
        this.authenticationSubject.next(true);
        return data;
      })
      .catch((error) => {
        // The toast is shown by the caller (LoginComponent), which classifies the Cognito
        // error (e.g. a disabled account) into a friendlier message — showing one here too
        // would duplicate it with the raw, less helpful Cognito message.
        this.authenticationSubject.next(false);
        throw error;
      });
  }

  clear() {
    if (this.subscribers.length > 0) {
      this.subscribers.forEach((sub) => {
        sub.unsubscribe();
      });
    }
    this.flush();
    this.utilService.clearStore();
  }

  /**
   * Auth.signOut() itself calls Cognito's global sign-out endpoint, which can fail — e.g. a
   * 400 Bad Request from https://cognito-idp.<region>.amazonaws.com when the refresh/access
   * token it's trying to invalidate is already expired or revoked. The user's intent was
   * still to log out, so the local logout must happen immediately regardless of whether
   * Cognito's own call succeeded — leaving them stuck on a dead session otherwise.
   */
  async signOut() {
    try {
      await Auth.signOut();
    } catch (error) {
      console.log('Auth.signOut() failed — logging out locally anyway: ', error);
    }
    this.clear();
    this.authenticationSubject.next(false);
    this.titleService.setTitle(false);
    this.router.navigate([LOGIN_PATH]);
  }

  updatePassword(newPassword: string, oldPassword: string, cognitoUser: any) {
    let picture = {};
    if (cognitoUser?.challengeParam?.requiredAttributes?.includes('picture')) {
      picture = { picture: 'src/assets/mockImg.jpg' };
    }
    const { email } = cognitoUser.challengeParam.userAttributes;
    return Auth.signIn(email, oldPassword)
      .then(() => {
        return Auth.completeNewPassword(cognitoUser, newPassword, picture);
      })
      .catch((error) => {
        if (error.code === 'NotAuthorizedException') {
          throw new Error('Invalid old password. Please enter the correct old password.');
        }
      });
  }

  isAuthenticated(): Promise<boolean> {
    if (this.authenticationSubject.value) {
      this.titleService.setTitle(true);
      return Promise.resolve(true);
    }
    // isRefreshTokenDefinitelyExpired() is only an estimate (see its doc comment) — it must
    // never decide authentication by itself, or a wrong guess wrongly logs out a still-valid
    // user. Cognito is always asked; the estimate only decides whether to clean up storage
    // once Cognito's own answer confirms the session really is dead, so a stale token left
    // over from a previous session stops causing a real Cognito call on every future page
    // load/app bootstrap (IOHB-NA).
    const suspectedExpired = this.isRefreshTokenDefinitelyExpired();
    return this.getUser()
      .then((user: any) => {
        const isAuthenticated = !!user;
        this.titleService.setTitle(isAuthenticated);
        if (!isAuthenticated && suspectedExpired) {
          this.clear();
        }
        return isAuthenticated;
      })
      .catch(() => {
        this.titleService.setTitle(false);
        if (suspectedExpired) {
          this.clear();
        }
        return false;
      });
  }

  async getCurrentUser() {
    this.subscribers.push(
      await this.userDataManager.user$.subscribe((user) => {
        if (user) {
          this.loggedInUser = user;
        }
      }),
    );
  }

  setToken(token: string) {
    if (token) localStorage.setItem(TOKEN_KEY, token);
  }

  getToken() {
    return localStorage.getItem(TOKEN_KEY) || null;
  }

  getUser(): Promise<any> {
    return Auth.currentUserInfo();
  }

  flush() {
    let userData = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('userSettings')) {
        const value = localStorage.getItem(key);
        if (value) {
          // Parse the stored data if needed (localStorage stores data as strings)
          const parsedValue = JSON.parse(value);
          userData.push({ key, value: parsedValue });
        }
      }
    }

    let visitData = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('visitSettings')) {
        const value = localStorage.getItem(key);
        if (value) {
          // Parse the stored data if needed (localStorage stores data as strings)
          const parsedValue = JSON.parse(value);
          visitData.push({ key, value: parsedValue });
        }
      }
    }

    const userSettings = this.localStorageService.get(this.loggedInUser?.aws_cognito_id);
    const visitSettings = this.localStorageService.get('visitSettings');
    localStorage.clear();
    this.localStorageService.set(this.loggedInUser?.aws_cognito_id, userSettings);

    userData.forEach((item) => {
      const { key, value } = item;
      this.localStorageService.set(key, JSON.stringify(value));
    });

    visitData.forEach((item) => {
      const { key, value } = item;
      this.localStorageService.set(key, JSON.stringify(value));
    });
  }

  /**
   * Verify TOTP code
   * @param user
   * @param code
   */
  async verifyTOTPCode(user: any, code: string): Promise<any> {
    try {
      return await Auth.confirmSignIn(user, code, 'SOFTWARE_TOKEN_MFA');
    } catch (error) {
      console.error('MFA verification failed:', error);
    throw error;
    }
  }
}
